import type { BucketDetail, BucketGraph, BucketSummary, Tone } from './types';
import { ApiError } from './http';
import { backend, type RegisteredBucket, type SystemCapabilities } from './backend';
import { DEMO } from './config';

/**
 * The API client boundary. One interface; two implementations:
 * demo (fixtures + latency, dev only) and live (the routes the backend
 * serves today, everything else surfacing its contracted 501).
 * The UI depends only on this interface.
 */
export interface ApiClient {
  listBuckets(signal?: AbortSignal): Promise<BucketSummary[]>;
  getBucket(id: string, signal?: AbortSignal): Promise<BucketDetail>;
  getGraph(id: string, signal?: AbortSignal): Promise<BucketGraph>;
  getCapabilities(signal?: AbortSignal): Promise<SystemCapabilities>;
}


/* ---------- live: honest mapping of the catalog the backend serves ---------- */

function liveTone(r: RegisteredBucket): Tone {
  const a = r.bucket.accessState;
  if (a === 'ACCESS_DENIED' || a === 'KMS_DENIED' || a === 'UNAVAILABLE') return 'bad';
  if (a === 'PARTIAL') return 'warn';
  return 'mut'; // registered, nothing analyzed: never rendered as ready
}

function liveLabel(r: RegisteredBucket): string {
  const a = r.bucket.accessState;
  if (a === 'KMS_DENIED') return 'Inaccessible · KMS';
  if (a === 'ACCESS_DENIED') return 'Access denied';
  if (a === 'UNAVAILABLE') return 'Unavailable';
  if (a === 'PARTIAL') return 'Partial access';
  const c = r.bucket.inventoryCoverage;
  if (c === 'NOT_STARTED') return 'Registered';
  return c.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (m) => m.toUpperCase());
}

function toSummary(r: RegisteredBucket): BucketSummary {
  return {
    id: r.bucket.id,
    name: r.bucket.name,
    company: r.bucket.companyId, // uuid until the companies route is data-bearing
    role: 'SOURCE',
    region: r.bucket.region,
    tools: '—', // never guessed from names
    coverage: r.bucket.inventoryCoverage === 'NOT_STARTED' ? 'UNSCANNED' : (r.bucket.inventoryCoverage as BucketSummary['coverage']),
    state: r.bucket.accessState === 'KMS_DENIED' ? 'kms-blocked' : r.bucket.accessState === 'ACCESS_DENIED' ? 'inaccessible' : 'unscanned',
    verdict: null,
    verdictLabel: liveLabel(r),
    tone: liveTone(r),
    sizeBytes: null,
    objectCount: null,
    analyzedAt: null,
  };
}

function toDetail(r: RegisteredBucket): BucketDetail {
  const blocked = r.bucket.accessState === 'ACCESS_DENIED' || r.bucket.accessState === 'KMS_DENIED';
  return {
    summary: toSummary(r),
    headline: blocked ? 'Registered; contents unreadable.' : 'Registered source scope. Analysis has not run.',
    stats: null,
    findings: [],
    entityClasses: [],
    dimensions: [],
    properties: [
      ['Account', r.accountId],
      ['Prefix', r.prefix || '(whole bucket)'],
      ['Region', r.bucket.region],
      ['Role', r.bucket.role],
      ['Access', r.bucket.accessState],
      ['Coverage', r.bucket.inventoryCoverage],
    ],
    readiness: null,
    explain: {
      happened: blocked
        ? 'The scope is registered but object reads are denied.'
        : 'The scope is registered in the catalog.',
      known: `Access ${r.bucket.accessState}, inventory ${r.bucket.inventoryCoverage}. These are persisted observations, not analysis.`,
      unknown: 'Whatever a sealed walk has not yet attributed. Every claim below comes from persisted runs, never inference.',
      matters: 'Claims about this dataset are exactly as strong as its sealed evidence.',
      next: blocked
        ? 'Access must be granted to the read-only role before anything can be verified.'
        : 'Sync re-observes on demand (starts within 2 minutes). Attribution walks dispatch automatically - within 10 minutes of dispatch, nightly for sweep-selected scopes. Open the graph to watch the walk live.',
      action: null,
    },
  };
}

/**
 * Product gating (handoff 3.2/17.1): a feature uses its EXACT capabilities
 * field, and the frontend must not call a tenant-gated route for data. The
 * gate error is thrown locally, labeled as such, never invented as backend.
 */
async function requireGate(field: 'sourceCatalog' | 'graph', signal?: AbortSignal): Promise<void> {
  const caps = await backend.getCapabilities(signal);
  if (caps.capabilities[field] !== 'IMPLEMENTED') {
    throw new ApiError(
      'CAPABILITY_UNAVAILABLE',
      `Product gate ${field} is ${caps.capabilities[field]}; the route is not requested.`,
      false,
      null,
      null,
      { capability: 'company_scoped_authorization', reason: `capabilities.${field} is not IMPLEMENTED; this surface is gated before any request is made.` },
    );
  }
}

const liveClient: ApiClient = {
  async listBuckets(signal) {
    await requireGate('sourceCatalog', signal);
    const out: BucketSummary[] = [];
    let cursor: string | undefined;
    // bounded pagination walk; server caps pageSize at 200
    for (let page = 0; page < 25; page++) {
      const res = await backend.listBuckets(200, cursor, undefined, signal);
      out.push(...res.items.map(toSummary));
      if (!res.nextCursor) break;
      cursor = res.nextCursor;
    }
    return out;
  },
  async getBucket(id, signal) {
    await requireGate('sourceCatalog', signal);
    return toDetail(await backend.getBucket(id, signal));
  },
  async getGraph(id, signal) {
    await requireGate('graph', signal);
    // The projection is not a BucketGraph: it carries no `spotlight`, which
    // the only consumer reads, so this cast would have produced a crash rather
    // than a graph. The route is only reachable in DEMO today, and the live
    // lens surface is DiscoveryGraph reading backend.getGraphProjection
    // directly. Refusing is the honest answer until a real adapter exists.
    void id;
    throw new Error(
      'The legacy graph shape has no live adapter. Use the discovery graph, which reads the projection directly.',
    );
  },
  getCapabilities(signal) {
    return backend.getCapabilities(signal);
  },
};

/**
 * DEMO is a build-time constant: in live builds (VITE_DEMO=0) the branch below
 * is statically false, the dynamic import is dead-code-eliminated, and no
 * fixture module reaches the bundle (verified by scripts/check-live-bundle.sh).
 */
export const api: ApiClient = DEMO ? (await import('./demo')).demoClient : liveClient;
